import { z } from "zod"

export const emailSchema = z
  .string()
  .trim()
  .min(1, "Email is required")
  .max(254, "Email must not exceed 254 characters")
  .toLowerCase()
  .regex(
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/,
    "Please enter a valid email address"
  )
  .refine((val) => !val.includes(".."), {
    message: "Email cannot contain consecutive dots",
  })

// ── Login ──────────────────────────────────────────────────────────────────────
// ACCOUNT_TYPES / UserRole are defined in the Signup section below — forward ref via z.enum
export const loginSchema = z.object({
  role: z.enum(["BUYER", "SELLER", "ADMIN"] as const, { error: "Please select a role" }),
  email: emailSchema,
  password: z.string().min(1, "Password is required"),
})

export type LoginFormValues = z.infer<typeof loginSchema>

// ── Signup ─────────────────────────────────────────────────────────────────────
export const ACCOUNT_TYPES = [
  { value: "BUYER", label: "BUYER" },
  { value: "SELLER", label: "SELLER" },
] as const

export type UserRole = (typeof ACCOUNT_TYPES)[number]["value"]

export const signupSchema = z.object({
  role: z.enum(
    ACCOUNT_TYPES.map((t) => t.value) as [UserRole, ...UserRole[]],
    { error: "Please select an account type" }
  ),
name: z
  .string()
  .trim()
  .min(1, "Full name is required")
  .min(2, "Full name must be at least 2 characters")
  .max(100, "Full name must not exceed 100 characters")
  .transform((val) => val.replace(/\s+/g, " ")) // collapse internal whitespace
  .pipe(
    z.string().regex(/^[a-zA-Z0-9\s'-]+$/, "Full name can only contain letters, numbers, spaces, hyphens, and apostrophes")
  ),
  email: emailSchema,
  password: z
  .string()
  .min(1, "Password is required")
  .min(8, "Password must be at least 8 characters")
  .max(72, "Password must not exceed 72 characters")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[0-9]/, "Password must contain at least one number")
  .regex(/[^a-zA-Z0-9]/, "Password must contain at least one special character")
  .refine((val) => val.trim() === val, {
    message: "Password must not start or end with a space",
  }),
})

export type SignupFormValues = z.infer<typeof signupSchema>
